import db from "../db/database";
import { crearRegistro } from "../utils/registro";

const registro = crearRegistro("campañas");

// Función para obtener todas las campañas activas
export const getAllActiveCampaigns = async () => {
  try {
    // Primero verificar si la tabla existe y tiene datos
    const count = await db.campaigns.count();

    if (count === 0) {
      return [];
    }

    // Intentar obtener todas las campañas sin filtro primero
    const allCampaigns = await db.campaigns.toArray();

    // Filtrar las no eliminadas manualmente
    const activeCampaigns = allCampaigns.filter((campaign) => !campaign.deleted);

    return activeCampaigns;
  } catch (error) {
    registro.error("No se pudieron leer las campañas activas:", error);

    // Si hay error, intentar devolver un array vacío
    try {
      const fallbackCampaigns = await db.campaigns.toArray();
      return fallbackCampaigns.filter((c) => !c.deleted);
    } catch (fallbackError) {
      registro.error("Tampoco se pudo leer la tabla completa:", fallbackError);
      return [];
    }
  }
};

// Función para buscar campañas por texto
export const searchCampaigns = async (searchText) => {
  try {
    if (!searchText) {
      return await getAllActiveCampaigns();
    }

    const campaigns = await db.campaigns
      .where("deleted")
      .notEqual(true)
      .filter(
        (campaign) =>
          campaign.Description?.toLowerCase().includes(searchText.toLowerCase()) ||
          campaign.No?.toLowerCase().includes(searchText.toLowerCase())
      )
      .toArray();

    return campaigns;
  } catch (error) {
    registro.error("Error al buscar campañas:", error);
    throw error;
  }
};

// Función para obtener una campaña por número
export const getCampaignByNo = async (campaignNo) => {
  try {
    const campaign = await db.campaigns
      .where("No")
      .equals(campaignNo)
      .and((campaign) => !campaign.deleted)
      .first();

    return campaign;
  } catch (error) {
    registro.error(`Error al obtener campaña ${campaignNo}:`, error);
    throw error;
  }
};

// Función para agregar una campaña
export const addCampaign = async (campaignData) => {
  try {
    const id = await db.campaigns.add({
      ...campaignData,
      pendingSync: false,
      lastModified: new Date(),
      lastSynced: new Date(),
      deleted: false,
    });

    return { id, ...campaignData };
  } catch (error) {
    registro.error("Error al agregar campaña:", error);
    throw error;
  }
};

// Función para actualizar una campaña
export const updateCampaign = async (id, campaignData) => {
  try {
    await db.campaigns.update(id, {
      ...campaignData,
      lastModified: new Date(),
    });

    return await db.campaigns.get(id);
  } catch (error) {
    registro.error(`Error al actualizar campaña ${id}:`, error);
    throw error;
  }
};

// Función para obtener campañas válidas para una fecha específica
export const getValidCampaignsForDate = async (date = new Date()) => {
  try {
    const campaigns = await getAllActiveCampaigns();
    const today = new Date(date);

    return campaigns.filter((campaign) => {
      // Si no hay fechas definidas, se considera siempre válida
      if (!campaign.Starting_Date && !campaign.Ending_Date) {
        return true;
      }

      let isValid = true;

      // Verificar fecha de inicio
      if (campaign.Starting_Date) {
        const startDate = parseCampaignDate(campaign.Starting_Date);
        if (startDate && today < startDate) {
          isValid = false;
        }
      }

      // Verificar fecha de fin
      if (campaign.Ending_Date) {
        const endDate = parseCampaignDate(campaign.Ending_Date);
        if (endDate && today > endDate) {
          isValid = false;
        }
      }

      return isValid;
    });
  } catch (error) {
    registro.error("Error al obtener campañas válidas:", error);
    throw error;
  }
};

// Función auxiliar para parsear fechas en formato DD/MM/YY
const parseCampaignDate = (dateString) => {
  if (!dateString) return null;

  try {
    // El formato viene como DD/MM/YY
    const parts = dateString.split("/");
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Los meses en JS van de 0-11
      let year = parseInt(parts[2], 10);

      // Si el año es de 2 dígitos, convertir a 4 dígitos
      if (year < 100) {
        year = year <= 30 ? 2000 + year : 1900 + year;
      }

      return new Date(year, month, day);
    }
  } catch (error) {
    registro.error(`Error al parsear fecha ${dateString}:`, error);
  }

  return null;
};

// Función para limpiar campañas obsoletas
export const cleanupCampaigns = async () => {
  try {
    await db.campaigns.clear();
    registro.log("Tabla de campañas vaciada");
  } catch (error) {
    registro.error("No se pudo vaciar la tabla de campañas:", error);
    throw error;
  }
};

// Función temporal para limpiar completamente la base de datos y resolver conflictos
export const resetDatabase = async () => {
  try {
    registro.warn("Eliminando la base de datos local para resolver un conflicto de esquema");
    await db.delete();
    registro.log("Base de datos eliminada; se recarga la página para recrearla");

    // Recargar la página para que se recree la base de datos
    window.location.reload();
  } catch (error) {
    registro.error("No se pudo eliminar la base de datos local:", error);
    throw error;
  }
};
